import { createContext, useCallback, useContext, useMemo, useState } from "react";
import type { ReactNode } from "react";
import type { CreationMethod } from "./CaseInformationStep/schema";
import type {
  ProfileDraft,
  ProfileMode,
  WizardCaseInfo,
  WizardProfileData,
  WizardState,
  WizardStep,
} from "./types";

const STEP_ORDER: WizardStep[] = ["case-information", "profile", "review"];

type InvestorWizardContextValue = WizardState & {
  goNext: () => void;
  goBack: () => void;
  goToStep: (step: WizardStep) => void;
  setCreationMethod: (method: CreationMethod) => void;
  setCaseInfo: (info: WizardCaseInfo) => void;
  setExistingProfile: (id: string, name: string, guid: string, isIaAssociated: boolean) => void;
  setProfileSearchActive: (active: boolean) => void;
  setProfileMode: (mode: ProfileMode) => void;
  setNewProfileData: (data: WizardProfileData) => void;
  setProfileDraft: (draft: ProfileDraft) => void;
  reset: () => void;
  footerContent: ReactNode;
  setFooterContent: (content: ReactNode) => void;
  isFullWidth: boolean;
  setIsFullWidth: (isFullWidth: boolean) => void;
};

const InvestorWizardContext = createContext<InvestorWizardContextValue | null>(null);

const INITIAL_STATE: WizardState = {
  currentStep: "case-information",
  creationMethod: "prospect",
  caseInfo: null,
  profileMode: null,
  selectedProfileId: null,
  selectedProfileGuid: null,
  selectedProfileName: null,
  selectedProfileIsIaAssociated: false,
  profileSearchActive: false,
  newProfileData: null,
  profileDraft: null,
};

export const InvestorWizardProvider = ({ children }: { children: React.ReactNode }) => {
  const [state, setState] = useState<WizardState>(INITIAL_STATE);
  const [footerContent, setFooterContent] = useState<ReactNode>(null);
  const [isFullWidth, setIsFullWidth] = useState(false);

  const goNext = useCallback(() => {
    setState((prev) => {
      const idx = STEP_ORDER.indexOf(prev.currentStep);
      const next = STEP_ORDER[idx + 1];
      return next ? { ...prev, currentStep: next } : prev;
    });
  }, []);

  const goBack = useCallback(() => {
    setState((prev) => {
      const idx = STEP_ORDER.indexOf(prev.currentStep);
      const prevStep = STEP_ORDER[idx - 1];
      return prevStep ? { ...prev, currentStep: prevStep } : prev;
    });
  }, []);

  const goToStep = useCallback((step: WizardStep) => {
    setState((prev) => ({ ...prev, currentStep: step }));
  }, []);

  const setCreationMethod = useCallback((method: CreationMethod) => {
    setState((prev) => ({ ...prev, creationMethod: method }));
  }, []);

  const setCaseInfo = useCallback((info: WizardCaseInfo) => {
    setState((prev) => ({ ...prev, caseInfo: info }));
  }, []);

  const setExistingProfile = useCallback(
    (id: string, name: string, guid: string, isIaAssociated: boolean) => {
      setState((prev) => ({
        ...prev,
        profileMode: "existing",
        selectedProfileId: id,
        selectedProfileGuid: guid,
        selectedProfileName: name,
        selectedProfileIsIaAssociated: isIaAssociated,
        newProfileData: null,
        profileDraft: null,
      }));
    },
    [],
  );

  const setProfileSearchActive = useCallback((active: boolean) => {
    setState((prev) => ({ ...prev, profileSearchActive: active }));
  }, []);

  const setProfileMode = useCallback((mode: ProfileMode) => {
    setState((prev) => ({ ...prev, profileMode: mode }));
  }, []);

  const setNewProfileData = useCallback((data: WizardProfileData) => {
    setState((prev) => ({
      ...prev,
      profileMode: "new",
      selectedProfileId: null,
      selectedProfileGuid: null,
      selectedProfileName: null,
      selectedProfileIsIaAssociated: false,
      newProfileData: data,
    }));
  }, []);

  const setProfileDraft = useCallback((draft: ProfileDraft) => {
    setState((prev) => ({ ...prev, profileDraft: draft }));
  }, []);

  const reset = useCallback(() => {
    setState(INITIAL_STATE);
    setFooterContent(null);
    setIsFullWidth(false);
  }, []);

  const value = useMemo<InvestorWizardContextValue>(
    () => ({
      ...state,
      goNext,
      goBack,
      goToStep,
      setCreationMethod,
      setCaseInfo,
      setExistingProfile,
      setProfileSearchActive,
      setProfileMode,
      setNewProfileData,
      setProfileDraft,
      reset,
      footerContent,
      setFooterContent,
      isFullWidth,
      setIsFullWidth,
    }),
    [
      state,
      goNext,
      goBack,
      goToStep,
      setCreationMethod,
      setCaseInfo,
      setExistingProfile,
      setProfileSearchActive,
      setProfileMode,
      setNewProfileData,
      setProfileDraft,
      reset,
      footerContent,
      isFullWidth,
    ],
  );

  return (
    <InvestorWizardContext.Provider value={value}>{children}</InvestorWizardContext.Provider>
  );
};

export const useInvestorWizard = (): InvestorWizardContextValue => {
  const ctx = useContext(InvestorWizardContext);
  if (!ctx) throw new Error("useInvestorWizard must be used inside InvestorWizardProvider");
  return ctx;
};
